import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, RefreshCw, Star, MessageSquare } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (s) => { try { return new Date(s).toLocaleString(); } catch { return s; } };

const Stars = ({ n }) => (
  <div className="inline-flex items-center gap-0.5">
    {[1,2,3,4,5].map((i) => (
      <Star key={i} className={`w-3.5 h-3.5 ${i <= (n || 0) ? "text-[#00FF88] fill-[#00FF88]" : "text-white/15"}`} />
    ))}
  </div>
);

const KV = ({ k, v }) => (
  <div>
    <div className="text-[10px] font-mono-c uppercase tracking-widest text-white/40">{k}</div>
    <div className="text-sm text-white/85 mt-0.5">{v || <span className="text-white/30">—</span>}</div>
  </div>
);

const PilotFeedback = ({ token }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/pilot/feedback/list`, { headers: { "X-Admin-Token": token } });
      setItems(data?.items || []);
    } catch {
      toast.error("Could not load feedback.");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  if (loading && items.length === 0) {
    return <div className="py-20 text-center text-white/40"><Loader2 className="w-6 h-6 animate-spin inline" /></div>;
  }

  return (
    <div data-testid="pilot-feedback-tab">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="font-mono-c text-[10px] uppercase tracking-[0.25em] text-[#00FF88]">// Post-pilot feedback</div>
          <div className="font-display font-extrabold text-2xl mt-1">{items.length} responses</div>
        </div>
        <button
          onClick={load}
          className="inline-flex items-center gap-2 text-xs font-bold border border-white/10 hover:border-white/30 text-white/70 hover:text-white rounded-full px-4 py-2"
          data-testid="pilot-feedback-refresh"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      {items.length === 0 ? (
        <div className="rounded-2xl border border-white/8 bg-white/[0.02] py-14 text-center">
          <MessageSquare className="w-8 h-8 mx-auto text-white/20 mb-3" />
          <div className="text-white/55 text-sm">No feedback submitted yet.</div>
          <div className="text-white/30 text-xs mt-1">After participants click "Mark Repaid" and you send the Thank-you + Survey email, responses will show here.</div>
        </div>
      ) : (
        <div className="space-y-4" data-testid="pilot-feedback-list">
          {items.map((f) => (
            <div key={f.id} className="rounded-2xl border border-white/8 bg-white/[0.02] p-5" data-testid={`pilot-feedback-row-${f.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3 mb-4 pb-3 border-b border-white/5">
                <div>
                  <div className="text-[10px] font-mono-c uppercase tracking-widest text-[#00FF88]">{f.application_id_ref}</div>
                  <div className="text-sm text-white/80 font-mono-c mt-0.5">{f.email}</div>
                </div>
                <div className="text-right">
                  <Stars n={f.helpfulness} />
                  <div className="text-[10px] font-mono-c text-white/35 mt-1">{fmtDate(f.created_at)}</div>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
                <KV k="What did you use it for?" v={f.used_for} />
                <KV k="Was the timeline reasonable?" v={f.timeline_reasonable} />
                <KV k="Would use again?" v={f.would_use_again} />
                <KV k="Would recommend?" v={f.would_recommend} />
                <KV k="Share anon for investors?" v={f.share_anon_in_investor_materials} />
                <KV k="Contact for testimonial?" v={f.contact_for_testimonial} />
                <div className="md:col-span-2">
                  <KV k="Improvements / what would they change?" v={f.improvements} />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PilotFeedback;
